import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import '../components/registration/RegistrationHistoryStep.css';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { RootStoreContext } from '../stores/RootStore';
import { FULL_PAGE } from '../components/SpinnerWrapper';
import { privateClassErrorMessage } from '../stores/ErrorMessages';
import {
  cancelPrivateClassBooking,
  getPrivateClassAvailabilityDates,
  getPrivateClassAvailabilitySlots,
  getPrivateClassBookings,
  getPrivateClassServices,
  getPrivateClassStyles,
  getPrivateClassTeachers,
  getPrivateClassesOverview,
  getPrivateCoinsHistory,
  newRequestId,
  postPrivateClassBooking,
  postPrivateClassQuote,
  PrivateClassBooking,
  PrivateClassQuoteResponse,
  PrivateClassSelectionRequest,
  PrivateClassService,
  PrivateClassSlot,
  PrivateClassStyle,
  PrivateClassTeacher,
  PrivateClassesOverviewResponse,
  PrivateCoinHistoryEvent
} from '../api/API';
import './PrivateClasses.css';
import PrivateCoinExpiry from '../components/PrivateCoinExpiry';
import '../components/InfoPopup.css';

type BookableSlot = PrivateClassSlot & { service_id?: number };

type PageMode = 'overview' | 'wizard' | 'history' | 'success';
export type WizardStep = 'style' | 'date' | 'details';

export const previousWizardStep = (step: WizardStep): WizardStep | null =>
  ({ style: null, date: 'style', details: 'date' } as const)[step];

export const teacherStartingPrice = (teacher: PrivateClassTeacher, services: PrivateClassService[]): number | null => {
  const prices = services.filter(service => service.teacher_id === teacher.id && service.pricing_complete)
    .flatMap(service => service.prices.map(price => price.total_private_coins));
  return teacher.minimum_private_coin_price ?? (prices.length ? Math.min(...prices) : null);
};

export const teacherSelectionPrice = (teacher: PrivateClassTeacher, services: PrivateClassService[], serviceId: number | null, personsCount: number): number | null =>
  services.find(service => service.teacher_id === teacher.id && service.id === serviceId && service.pricing_complete)
    ?.prices.find(price => price.persons_count === personsCount)?.total_private_coins ?? null;

const RESUME_STORAGE_KEY = 'private-classes-booking-resume';
const PERSON_OPTIONS = [1, 2, 3, 4];

interface ResumeDraft {
  token: string;
  teacherIds: number[];
  selection: PrivateClassSelectionRequest;
}

interface PrivateClassesProps {
  embedded?: boolean;
  onExit?: () => void;
}

export const chooseDefaultStyle = (
  styles: PrivateClassStyle[],
  preferredStyleId?: number | null
): PrivateClassStyle | null => {
  const sorted = [...styles].sort((left, right) =>
    (left.priority ?? Number.MAX_SAFE_INTEGER) - (right.priority ?? Number.MAX_SAFE_INTEGER)
      || left.name.localeCompare(right.name)
      || left.id - right.id
  );
  return sorted.find(style => style.id === preferredStyleId)
    || sorted.find(style => style.default)
    || sorted[0]
    || null;
};

export const filterTeachersForStyle = (
  teachers: PrivateClassTeacher[],
  styleId?: number | null
): PrivateClassTeacher[] => [...teachers]
  .filter(teacher => styleId == null || (teacher.styles || []).some(style => style.id === styleId))
  .sort((left, right) => left.name.localeCompare(right.name) || left.id - right.id);

export const sortSlotsChronologically = (slots: PrivateClassSlot[]): PrivateClassSlot[] =>
  [...slots].sort((left, right) =>
    new Date(left.starts_at).getTime() - new Date(right.starts_at).getTime()
      || left.teacher.name.localeCompare(right.teacher.name)
  );

export const groupSlotsByDay = (slots: PrivateClassSlot[]): Record<string, PrivateClassSlot[]> =>
  sortSlotsChronologically(slots).reduce<Record<string, PrivateClassSlot[]>>((groups, slot) => {
    const day = moment.parseZone(slot.starts_at).format('YYYY-MM-DD');
    groups[day] = [...(groups[day] || []), slot];
    return groups;
  }, {});

export const mapCalendarDots = (dates: string[]): Record<string, boolean> =>
  dates.reduce<Record<string, boolean>>((dots, date) => {
    dots[date] = true;
    return dots;
  }, {});

const readResumeDraft = (): ResumeDraft | null => {
  try {
    const raw = sessionStorage.getItem(RESUME_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeResumeDraft = (draft: ResumeDraft) => {
  try {
    sessionStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // A blocked sessionStorage should not prevent the user from buying coins.
  }
};

const clearResumeDraft = () => {
  try {
    sessionStorage.removeItem(RESUME_STORAGE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
};

const errorCode = (error: any): string => String(error?.code || error?.error || error?.message || '').toUpperCase();

const emptyOverview = (userId: number): PrivateClassesOverviewResponse => ({
  user_id: userId,
  private_coin_balance: 0,
  private_coin_buckets: [],
  default_style_id: null,
  upcoming_bookings: [],
  packages_path: '/packages-membership'
});

const PrivateClasses = observer(({ embedded = false, onExit }: PrivateClassesProps) => {
  const store = useContext(RootStoreContext);
  const navigate = useNavigate();
  const userId = store.registrationStore.user?.id;
  const [mode, setMode] = useState<PageMode>('overview');
  const [step, setStep] = useState<WizardStep>('style');
  const [overview, setOverview] = useState<PrivateClassesOverviewResponse | null>(null);
  const [styles, setStyles] = useState<PrivateClassStyle[]>([]);
  const [teachers, setTeachers] = useState<PrivateClassTeacher[]>([]);
  const [services, setServices] = useState<PrivateClassService[]>([]);
  const availabilityServices = useRef<PrivateClassService[]>([]);
  const availabilityRequestId = useRef(0);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [slots, setSlots] = useState<BookableSlot[]>([]);
  const [historyBookings, setHistoryBookings] = useState<PrivateClassBooking[]>([]);
  const [coinHistory, setCoinHistory] = useState<PrivateCoinHistoryEvent[]>([]);
  const [selectedStyleId, setSelectedStyleId] = useState<number | null>(null);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<number[]>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<BookableSlot | null>(null);
  const [personsCount, setPersonsCount] = useState(1);
  const [quote, setQuote] = useState<PrivateClassQuoteResponse | null>(null);
  const [successBooking, setSuccessBooking] = useState<PrivateClassBooking | null>(null);
  const [bookingToCancel, setBookingToCancel] = useState<PrivateClassBooking | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => moment().startOf('month'));
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [showBookingTerms, setShowBookingTerms] = useState(false);
  const [teacherInfo, setTeacherInfo] = useState<PrivateClassTeacher | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const timesRef = useRef<HTMLElement>(null);
  const termsDialogRef = useRef<HTMLElement>(null);
  const termsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const termsCloseRef = useRef<HTMLButtonElement>(null);
  const teacherDialogRef = useRef<HTMLElement>(null);
  const teacherInfoTriggerRef = useRef<HTMLButtonElement | null>(null);
  const teacherCloseRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => () => { availabilityRequestId.current += 1; }, []);

  const selectedStyle = styles.find(style => style.id === selectedStyleId) || null;
  const styleTeachers = useMemo(
    () => filterTeachersForStyle(teachers, selectedStyleId),
    [teachers, selectedStyleId]
  );
  const calendarDots = useMemo(() => mapCalendarDots(availableDates), [availableDates]);
  const chronologicalSlots = useMemo(() => sortSlotsChronologically(slots), [slots]);
  const styleServices = useMemo(() => services
    .filter(service => styleTeachers.some(teacher => teacher.id === service.teacher_id) && service.pricing_complete)
    .sort((left, right) => (left.priority ?? Number.MAX_SAFE_INTEGER) - (right.priority ?? Number.MAX_SAFE_INTEGER)
      || left.name.localeCompare(right.name)), [styleTeachers, services]);
  const serviceOptions = styleServices.filter((service, index, all) => all.findIndex(item => item.id === service.id) === index);
  const visibleTeachers = styleTeachers.filter(teacher => styleServices.some(service =>
    service.teacher_id === teacher.id && service.id === selectedServiceId));

  useEffect(() => {
    if (!styleServices.some(service => service.id === selectedServiceId)) {
      setSelectedServiceId(styleServices[0]?.id ?? null);
    }
  }, [styleServices, selectedServiceId]);

  useEffect(() => {
    if (mode === 'wizard') toolbarRef.current?.scrollIntoView?.({ block: 'start' });
  }, [mode, step]);

  useEffect(() => {
    if (selectedDate) {
      timesRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedDate]);

  const selectedService = services.find(service =>
    service.id === selectedServiceId
      && (!selectedSlot || service.teacher_id === selectedSlot.teacher.id)
  ) || null;

  useEffect(() => {
    if (!bookingToCancel) return;
    cancelButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && busyAction !== `cancel-${bookingToCancel.id}`) {
        setBookingToCancel(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [bookingToCancel, busyAction]);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    const loadInitial = async () => {
      setLoading(true);
      setError(null);
      store.spinnerStore.show(FULL_PAGE);
      try {
        const [overviewResponse, stylesResponse, teachersResponse, servicesResponse] = await Promise.all([
          getPrivateClassesOverview(userId),
          getPrivateClassStyles(userId),
          getPrivateClassTeachers(userId),
          getPrivateClassServices(userId)
        ]);
        if (!active) return;
        setOverview(overviewResponse);
        setStyles(stylesResponse.styles);
        setTeachers(teachersResponse.teachers);
        setServices(servicesResponse.services);
        const defaultStyle = chooseDefaultStyle(
          stylesResponse.styles,
          stylesResponse.default_style_id ?? overviewResponse.default_style_id
        );
        setSelectedStyleId(defaultStyle?.id ?? null);
        const defaultTeachers = filterTeachersForStyle(teachersResponse.teachers, defaultStyle?.id);
        setSelectedTeacherIds(defaultTeachers.map(teacher => teacher.id));

        const shouldResume = new URLSearchParams(window.location.search).get('resume') === '1';
        const draft = shouldResume ? readResumeDraft() : null;
        if (draft) {
          await restoreAfterCheckout(draft, overviewResponse, servicesResponse.services);
        }
      } catch (caught: any) {
        if (!active) return;
        setFeatureDisabled(errorCode(caught).includes('FEATURE_DISABLED'));
        setError(privateClassErrorMessage(caught));
      } finally {
        if (active) setLoading(false);
        store.spinnerStore.hide(FULL_PAGE);
      }
    };
    loadInitial();
    return () => {
      active = false;
      store.spinnerStore.hide(FULL_PAGE);
    };
  }, [userId]);

  const restoreAfterCheckout = async (
    draft: ResumeDraft,
    currentOverview: PrivateClassesOverviewResponse,
    loadedServices: PrivateClassService[]
  ) => {
    if (!userId || draft.selection.userId !== userId) {
      clearResumeDraft();
      return;
    }

    const date = moment.parseZone(draft.selection.startsAt).format('YYYY-MM-DD');
    const [freshOverview, datesResponse, slotsResponse, freshQuote] = await Promise.all([
      getPrivateClassesOverview(userId),
      getPrivateClassAvailabilityDates(userId, [draft.selection.teacherId], draft.selection.serviceId),
      getPrivateClassAvailabilitySlots(userId, [draft.selection.teacherId], draft.selection.serviceId, date),
      postPrivateClassQuote(draft.selection)
    ]);
    const matchingSlot = slotsResponse.slots.find(slot =>
      slot.starts_at === draft.selection.startsAt
        && slot.teacher.id === draft.selection.teacherId
        && slot.venue.id === draft.selection.venueId
    );

    setOverview(freshOverview || currentOverview);
    setSelectedStyleId(draft.selection.styleId);
    setSelectedTeacherIds(draft.teacherIds);
    setSelectedServiceId(draft.selection.serviceId);
    setPersonsCount(draft.selection.personsCount);
    setSelectedDate(date);
    availabilityServices.current = loadedServices.filter(service => service.id === draft.selection.serviceId && service.teacher_id === draft.selection.teacherId);
    setAvailableDates(datesResponse.dates);
    setSlots(slotsResponse.slots.map(slot => ({ ...slot, service_id: draft.selection.serviceId })));
    setCalendarMonth(moment(date).startOf('month'));
    setMode('wizard');
    window.history.replaceState(window.history.state, '', window.location.pathname);

    if (!matchingSlot || !loadedServices.some(service =>
      service.id === draft.selection.serviceId && service.teacher_id === draft.selection.teacherId
    )) {
      setSelectedSlot(null);
      setQuote(null);
      setStep('date');
      setError('Your previous time is no longer available. Please choose another time.');
      return;
    }

    setSelectedSlot({ ...matchingSlot, service_id: draft.selection.serviceId });
    setQuote(freshQuote);
    setStep('details');
    setMessage(freshQuote.sufficient_balance
      ? 'Balance refreshed and your selection was requoted. Review it before booking.'
      : 'Your balance was refreshed, but you still need more Private Coins for this booking.');
  };

  const refreshOverview = async () => {
    if (!userId) return;
    const response = await getPrivateClassesOverview(userId);
    setOverview(response);
    window.dispatchEvent(new Event('private-coins-changed'));
  };

  const startWizard = () => {
    availabilityRequestId.current += 1;
    setBusyAction(null);
    const defaultStyle = chooseDefaultStyle(styles, overview?.default_style_id);
    const defaultTeachers = filterTeachersForStyle(teachers, defaultStyle?.id);
    setSelectedStyleId(defaultStyle?.id ?? null);
    setSelectedTeacherIds(defaultTeachers.map(teacher => teacher.id));
    setSelectedServiceId(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setPersonsCount(1);
    setQuote(null);
    setMessage(null);
    setError(null);
    setStep('style');
    setMode('wizard');
  };

  const selectStyle = (styleId: number) => {
    const matchingTeachers = filterTeachersForStyle(teachers, styleId);
    setSelectedStyleId(styleId);
    setSelectedTeacherIds(matchingTeachers.map(teacher => teacher.id));
  };

  const continueFromTeachers = async () => {
    if (!userId || selectedTeacherIds.length === 0) return;
    const options = services.filter(item => selectedTeacherIds.includes(item.teacher_id) && visibleTeachers.some(teacher => teacher.id === item.teacher_id)
      && item.id === selectedServiceId && item.pricing_complete);
    if (!options.length) {
      setError('No bookable Private Class Option is available for the selected teachers.');
      return;
    }
    await loadAvailableDates(options);
  };

  const loadAvailableDates = async (options: PrivateClassService[]) => {
    if (!userId) return;
    const requestId = ++availabilityRequestId.current;
    setBusyAction('dates');
    setError(null);
    try {
      const responses = await Promise.all(options.map(option =>
        getPrivateClassAvailabilityDates(userId, [option.teacher_id], option.id)));
      if (requestId !== availabilityRequestId.current) return;
      const dates = Array.from(new Set(responses.flatMap(response => response.dates))).sort();
      availabilityServices.current = options;
      setAvailableDates(dates);
      setSelectedDate(null);
      setSelectedSlot(null);
      setSlots([]);
      setQuote(null);
      if (dates[0]) setCalendarMonth(moment(dates[0]).startOf('month'));
      setStep('date');
    } catch (caught: any) {
      if (requestId === availabilityRequestId.current) setError(privateClassErrorMessage(caught));
    } finally {
      if (requestId === availabilityRequestId.current) setBusyAction(null);
    }
  };

  const fetchAvailableSlots = async (date: string): Promise<BookableSlot[]> => {
    if (!userId) return [];
    const responses = await Promise.all(availabilityServices.current.map(async option => {
      const response = await getPrivateClassAvailabilitySlots(userId, [option.teacher_id], option.id, date);
      return response.slots.map(slot => ({ ...slot, service_id: option.id }));
    }));
    return responses.flat();
  };

  const chooseDate = async (date: string) => {
    if (!userId) return;
    const requestId = ++availabilityRequestId.current;
    setSlots([]);
    setSelectedDate(date);
    setSelectedSlot(null);
    setQuote(null);
    setBusyAction(`date-${date}`);
    setError(null);
    try {
      const nextSlots = await fetchAvailableSlots(date);
      if (requestId === availabilityRequestId.current) setSlots(nextSlots);
    } catch (caught: any) {
      if (requestId === availabilityRequestId.current) setError(privateClassErrorMessage(caught));
    } finally {
      if (requestId === availabilityRequestId.current) setBusyAction(null);
    }
  };

  const chooseSlot = (slot: BookableSlot) => {
    const service = services.find(item =>
      item.id === slot.service_id && item.teacher_id === slot.teacher.id
    ) || services.find(item => item.teacher_id === slot.teacher.id && item.pricing_complete);
    setSelectedSlot(slot);
    setSelectedServiceId(service?.id ?? null);
    setQuote(null);
    setStep('details');
  };

  const selectionRequest = (): PrivateClassSelectionRequest | null => {
    if (!userId || !selectedStyleId || !selectedServiceId || !selectedSlot) return null;
    return {
      userId,
      teacherId: selectedSlot.teacher.id,
      styleId: selectedStyleId,
      serviceId: selectedServiceId,
      startsAt: selectedSlot.starts_at,
      venueId: selectedSlot.venue.id,
      personsCount
    };
  };

  useEffect(() => {
    const request = selectionRequest();
    if (mode !== 'wizard' || step !== 'details' || !request) return;
    let active = true;
    setQuote(null);
    setBusyAction('quote');
    setError(null);
    postPrivateClassQuote(request).then(response => {
      if (active) setQuote(response);
    }).catch(caught => {
      if (active) setError(privateClassErrorMessage(caught));
    }).finally(() => {
      if (active) setBusyAction(null);
    });
    return () => { active = false; };
  }, [mode, step, userId, selectedStyleId, selectedServiceId, selectedSlot, personsCount]);

  const buyPrivateCoins = () => {
    const request = selectionRequest();
    if (!request || !quote?.resume_token) return;
    writeResumeDraft({ token: quote.resume_token, teacherIds: selectedTeacherIds, selection: request });
    navigate(
      `${overview?.packages_path || '/packages-membership'}?tab=private&return_to=private-classes&resume_token=${encodeURIComponent(quote.resume_token)}`
    );
  };

  const refreshSlotsAfterConflict = async () => {
    if (!userId || !selectedDate || !selectedServiceId) return;
    setSlots(await fetchAvailableSlots(selectedDate));
    setSelectedSlot(null);
    setQuote(null);
    setStep('date');
  };

  const book = async () => {
    const request = selectionRequest();
    if (!request || !quote) return;
    setBusyAction('book');
    setError(null);
    try {
      const response = await postPrivateClassBooking(request, quote.cost, newRequestId('private-class'));
      if (!response.booking) throw new Error('The booking response was empty.');
      clearResumeDraft();
      setSuccessBooking(response.booking);
      setMessage('Your private class is booked.');
      setMode('overview');
      await refreshOverview();
    } catch (caught: any) {
      const code = errorCode(caught);
      setError(privateClassErrorMessage(caught));
      if (code.includes('TIME_SLOT_UNAVAILABLE') || code.includes('PRICE_CHANGED')) {
        try {
          await refreshSlotsAfterConflict();
        } catch (refreshError: any) {
          setError(privateClassErrorMessage(refreshError));
        }
      }
    } finally {
      setBusyAction(null);
    }
  };

  const openHistory = async () => {
    if (!userId) return;
    setMode('history');
    setBusyAction('history');
    setError(null);
    try {
      const [bookingsResponse, coinsResponse] = await Promise.all([
        getPrivateClassBookings(userId, 'history'),
        getPrivateCoinsHistory(userId)
      ]);
      setHistoryBookings(bookingsResponse.bookings);
      setCoinHistory(coinsResponse.events);
    } catch (caught: any) {
      setError(privateClassErrorMessage(caught));
    } finally {
      setBusyAction(null);
    }
  };

  const confirmCancellation = async () => {
    if (!userId || !bookingToCancel) return;
    const booking = bookingToCancel;
    setBusyAction(`cancel-${booking.id}`);
    setError(null);
    try {
      const response = await cancelPrivateClassBooking(userId, booking.id, 'Cancelled by student');
      setMessage(`Private class cancelled. ${response.refunded_coins} PȻ returned to your balance.`);
      setBookingToCancel(null);
      await refreshOverview();
      if (mode === 'history') await openHistory();
    } catch (caught: any) {
      setError(privateClassErrorMessage(caught));
    } finally {
      setBusyAction(null);
    }
  };

  const goBack = () => {
    if (showBookingTerms) { setShowBookingTerms(false); return; }
    if (teacherInfo) { setTeacherInfo(null); return; }
    if (bookingToCancel) { setBookingToCancel(null); return; }
    availabilityRequestId.current += 1;
    setBusyAction(null);
    setError(null);
    setMessage(null);
    if (mode === 'overview') {
      if (embedded && onExit) {
        onExit();
        return;
      }
      navigate('/', { replace: true });
      return;
    }
    if (mode === 'history' || mode === 'success') {
      setMode('overview');
      return;
    }
    const previousStep = previousWizardStep(step);
    if (previousStep) setStep(previousStep);
    else setMode('overview');
  };

  // Browser/OS Back must use the same toolbar position as the app Back button.
  // Otherwise the browser restores the previous time-list scroll after goBack.
  useEffect(() => {
    if (mode !== 'wizard') return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = previous; };
  }, [mode]);

  const backSentinel = useRef(false);
  const internalBackActive = mode === 'wizard' || showBookingTerms || !!teacherInfo || !!bookingToCancel;
  useEffect(() => {
    if (internalBackActive && !backSentinel.current) {
      window.history.pushState(window.history.state, '', window.location.href);
      backSentinel.current = true;
    }

  }, [internalBackActive]);

  // A same-URL history entry lets Android/browser Back follow the wizard without
  // leaving this route. Capture prevents the embedded home-page handler restarting check-in.
  useEffect(() => {
    if (!backSentinel.current) return;
    const onNativeBack = (event: PopStateEvent) => {
      event.stopImmediatePropagation();
      if (!internalBackActive) {
        backSentinel.current = false;
        window.removeEventListener('popstate', onNativeBack, true);
        goBack();
        return;
      }
      const exitsInternalNavigation = !showBookingTerms && !teacherInfo && !bookingToCancel && mode === 'wizard' && step === 'style'
        || mode !== 'wizard';
      if (exitsInternalNavigation) backSentinel.current = false;
      else window.history.pushState(window.history.state, '', window.location.href);
      goBack();
    };
    window.addEventListener('popstate', onNativeBack, true);
    return () => window.removeEventListener('popstate', onNativeBack, true);
  }, [mode, step, teacherInfo, bookingToCancel, showBookingTerms, internalBackActive]);

  useEffect(() => {
    if (!teacherInfo) return;
    const trigger = teacherInfoTriggerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    teacherCloseRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setTeacherInfo(null);
      if (event.key === 'Tab') {
        const focusable = teacherDialogRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex="0"]');
        const first = focusable?.[0];
        const last = focusable?.[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    const containFocus = (event: FocusEvent) => {
      if (!teacherDialogRef.current?.contains(event.target as Node)) teacherCloseRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', containFocus);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', containFocus);
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, [teacherInfo]);

  useEffect(() => {
    if (!showBookingTerms) return;
    const trigger = termsTriggerRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    termsCloseRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setShowBookingTerms(false); }
      if (event.key !== 'Tab') return;
      const focusable = termsDialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
      );
      const first = focusable?.[0];
      const last = focusable?.[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    const containFocus = (event: FocusEvent) => {
      if (!termsDialogRef.current?.contains(event.target as Node)) termsCloseRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', containFocus);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', containFocus);
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, [showBookingTerms]);

  const renderToolbar = (title: string) => (
    <div className="pc-toolbar" ref={toolbarRef}>
      <button className="secondary-nav-button" type="button" onClick={goBack}><FontAwesomeIcon icon={faChevronLeft} /><span>Back</span></button>
      <h1>{title}</h1>
      <span aria-hidden="true" />
    </div>
  );

  const renderFeedback = () => (
    <>
      {message && <div className="pc-banner pc-banner-success" role="status">{message}</div>}
      {error && <div className="pc-banner pc-banner-error" role="alert">{error}</div>}
    </>
  );

  const renderBooking = (booking: PrivateClassBooking, allowCancel = true) => (
    <article className="pc-booking-card" key={booking.id}>
      <div className="pc-booking-date">
        <strong>{moment.parseZone(booking.starts_at).format('D MMM')}</strong>
        <span>{moment.parseZone(booking.starts_at).format('HH:mm')}</span>
      </div>
      <div className="pc-booking-details">
        <strong>{booking.service.name} with {booking.teacher.name}</strong>
        <span>{booking.style.name} · {booking.venue.name}, {booking.room.name}</span>
        <span>{booking.persons_count} {booking.persons_count === 1 ? 'student' : 'students'} · {booking.cost} PȻ</span>
        {booking.status === 'cancelled' && <span className="pc-status pc-status-cancelled">Cancelled</span>}
      </div>
      {allowCancel && booking.can_cancel && booking.status === 'confirmed' && (
        <button className="pc-danger-button" type="button" onClick={() => setBookingToCancel(booking)}>
          Cancel
        </button>
      )}
    </article>
  );

  const renderOverview = () => (
    <>
      {renderToolbar('Private Classes')}
      {renderFeedback()}
      <section className="pc-balance-card" aria-label="Private Coin balance">
        <div>
          <span>Private Coin balance</span>
          <strong>{overview?.private_coin_balance ?? 0} PȻ</strong>
          <PrivateCoinExpiry buckets={overview?.private_coin_buckets} />
        </div>
        <button type="button" className="pc-secondary-button" onClick={() => navigate('/packages-membership?tab=private')}>
          Get Private Coins
        </button>
      </section>

      <section className="pc-hero">
        <div>
          <h2>Get personalized training</h2>
          <p>The fastest progress. Train 1:1 or as a couple with your favorite instructor's full attention. Pick a time to get started.</p>
        </div>
        <button className="pc-primary-button pc-hero-action" type="button" onClick={startWizard} disabled={styles.length === 0}>
          Book Private Class
        </button>
      </section>

      <section className="pc-section">
        <div className="pc-section-heading">
          <h2>Upcoming bookings</h2>
          <button className="pc-link-button" type="button" onClick={openHistory}>History</button>
        </div>
        {overview?.upcoming_bookings.length ? (
          <div className="pc-booking-list">{overview.upcoming_bookings.map(booking => renderBooking(booking))}</div>
        ) : (
          <div className="pc-empty">No upcoming private classes yet.</div>
        )}
      </section>
    </>
  );

  const renderProgress = () => {
    const labels = ['Style + Teacher', 'Date + Time', 'Details + Payment'];
    const steps: WizardStep[] = ['style', 'date', 'details'];
    const current = steps.indexOf(step);
    return (
      <ol className="pc-progress" aria-label="Booking progress">
        {labels.map((label, index) => (
          <li className={index === current ? 'active' : index < current ? 'complete' : ''} key={label}>
            <span>{index + 1}</span>{label}
          </li>
        ))}
      </ol>
    );
  };

  const renderStyleStep = () => (
    <section className="pc-wizard-card">
      <span className="pc-kicker">Step 1</span>
      <h2>Which dance style?</h2>
      <div className="pc-pill-list" role="radiogroup" aria-label="Dance style">
        {styles.map((style, index) => (
          <button
            key={style.id}
            type="button"
            role="radio"
            disabled={busyAction === 'dates'}
            aria-checked={selectedStyleId === style.id}
            className={selectedStyleId === style.id ? 'selected' : ''}
            onClick={() => selectStyle(style.id)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                selectStyle(style.id);
                return;
              }
              if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
              event.preventDefault();
              const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
              const nextIndex = (index + direction + styles.length) % styles.length;
              selectStyle(styles[nextIndex].id);
              (event.currentTarget.parentElement?.children[nextIndex] as HTMLElement | undefined)?.focus();
            }}
          >
            {style.name}
          </button>
        ))}
      </div>
      {styles.length === 0 && <div className="pc-empty">No private-class styles are available.</div>}
      <label className="pc-field">
        <span>Private Class Option</span>
        <select disabled={busyAction === 'dates'} value={selectedServiceId ?? ''} onChange={event => setSelectedServiceId(Number(event.target.value))}>
          {serviceOptions.map(service => (
            <option key={`${service.teacher_id}-${service.id}`} value={service.id}>
              {service.name} · {service.duration_minutes} min
            </option>
          ))}
        </select>
      </label>
      <fieldset className="pc-persons">
        <legend>Number of Students</legend>
        <div className="pc-pill-list">
          {PERSON_OPTIONS.map(count => (
            <button
              key={count}
              type="button"
              disabled={busyAction === 'dates'}
              aria-pressed={personsCount === count}
              className={personsCount === count ? 'selected' : ''}
              onClick={() => { setPersonsCount(count); setQuote(null); }}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setPersonsCount(count);
                  setQuote(null);
                }
              }}
            >
              {count}
            </button>
          ))}
        </div>
      </fieldset>

    </section>
  );

  const toggleTeacher = (teacherId: number) => {
    setSelectedTeacherIds(ids => ids.includes(teacherId)
      ? ids.filter(id => id !== teacherId)
      : [...ids, teacherId]);
  };

  const renderTeacherStep = () => {
    const allSelected = visibleTeachers.length > 0 && visibleTeachers.every(teacher => selectedTeacherIds.includes(teacher.id));
    return (
      <section className="pc-wizard-card">
        <div className="pc-step-heading">
          <div><h2>Choose teachers</h2></div>
          <button
            className="pc-link-button"
            type="button"
            disabled={busyAction === 'dates'}
            onClick={() => setSelectedTeacherIds(allSelected ? [] : visibleTeachers.map(teacher => teacher.id))}
          >
            {allSelected ? 'Unselect all' : 'Select all'}
          </button>
        </div>
        <p className="pc-muted">We’ll show times for every selected teacher.</p>
        <div className="pc-teacher-grid">
          {visibleTeachers.map(teacher => {
            const selected = selectedTeacherIds.includes(teacher.id);
            return (
              <div key={teacher.id} className={`pc-teacher-container pc-teacher-card ${selected ? 'selected' : ''}`}>
                <button
                  type="button"
                  aria-pressed={selected}
                  disabled={busyAction === 'dates'}
                  className="pc-teacher-select"
                  aria-label={`Select ${teacher.name}`}
                  onClick={() => toggleTeacher(teacher.id)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleTeacher(teacher.id);
                    }
                  }}
                >
                  {teacher.square_profile_photo_url ? <img src={teacher.square_profile_photo_url} alt="" /> : <span className="pc-avatar">{teacher.name.charAt(0)}</span>}
                </button>
                <span className="pc-teacher-copy">
                  <span className="pc-teacher-name"><strong>{teacher.name}</strong>
                    {teacher.private_class_description?.trim() && (
                      <button type="button" className="pc-teacher-info" aria-label={`About ${teacher.name}`}
                        onClick={event => { teacherInfoTriggerRef.current = event.currentTarget; setTeacherInfo(teacher); }}>ⓘ</button>
                    )}
                  </span>
                  <small>{teacherSelectionPrice(teacher, services, selectedServiceId, personsCount) != null ? `${teacherSelectionPrice(teacher, services, selectedServiceId, personsCount)} PȻ` : 'No options available'}</small>
                </span>
                <span className="pc-check" aria-hidden="true">{selected ? '✓' : ''}</span>
              </div>
            );
          })}
        </div>
        {visibleTeachers.length === 0 && <div className="pc-empty">No teachers offer {selectedStyle?.name || 'this style'}.</div>}
        <div className="pc-step-actions">
          <button
            className="pc-primary-button"
            type="button"
            disabled={!visibleTeachers.some(teacher => selectedTeacherIds.includes(teacher.id)) || busyAction === 'dates'}
            onClick={continueFromTeachers}
          >
            {busyAction === 'dates' ? 'Loading availability…' : 'Choose a date'}
          </button>
        </div>
      </section>
    );
  };

  const renderCalendar = () => {
    const start = calendarMonth.clone().startOf('month');
    const leading = (start.isoWeekday() + 6) % 7;
    const days = Array.from({ length: start.daysInMonth() }, (_, index) => start.clone().date(index + 1));
    return (
      <section className="pc-wizard-card">
        <span className="pc-kicker">Step 2</span>
        <h2>Choose a date</h2>
        <div className="pc-calendar">
          <div className="pc-calendar-head">
            <button type="button" aria-label="Previous month" onClick={() => setCalendarMonth(month => month.clone().subtract(1, 'month'))}>‹</button>
            <strong>{calendarMonth.format('MMMM YYYY')}</strong>
            <button type="button" aria-label="Next month" onClick={() => setCalendarMonth(month => month.clone().add(1, 'month'))}>›</button>
          </div>
          <div className="pc-calendar-weekdays" aria-hidden="true">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => <span key={day}>{day}</span>)}
          </div>
          <div className="pc-calendar-days">
            {Array.from({ length: leading }, (_, index) => <span key={`blank-${index}`} />)}
            {days.map(day => {
              const date = day.format('YYYY-MM-DD');
              const available = !!calendarDots[date];
              return (
                <button
                  key={date}
                  type="button"
                  disabled={!available || busyAction === `date-${date}`}
                  className={selectedDate === date ? 'selected' : ''}
                  aria-label={`${day.format('D MMMM')}${available ? ', available' : ', unavailable'}`}
                  onClick={() => chooseDate(date)}
                >
                  {day.date()}
                  {available && <span className="pc-calendar-dot" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
        {availableDates.length === 0 && <div className="pc-empty">No available dates for these teachers right now.</div>}
      </section>
    );
  };

  const renderTimeStep = () => (
    <section className="pc-wizard-card" ref={timesRef}>
      <h2>Choose a time</h2>
      {selectedDate && <p className="pc-muted">{moment(selectedDate).format('dddd, D MMMM')}</p>}
      {busyAction === `date-${selectedDate}` && <p role="status">Loading available times…</p>}
      <div className="pc-slot-list" aria-busy={busyAction === `date-${selectedDate}`}>
        {chronologicalSlots.map(slot => (
          <button
            key={`${slot.starts_at}-${slot.teacher.id}-${slot.venue.id}-${(slot as BookableSlot).service_id}`}
            type="button"
            className="pc-slot-card"
            onClick={() => chooseSlot(slot)}
          >
            <strong>{moment.parseZone(slot.starts_at).format('HH:mm')}</strong>
            {slot.teacher.square_profile_photo_url ? <img src={slot.teacher.square_profile_photo_url} alt="" /> : <span className="pc-avatar pc-avatar-small">{slot.teacher.name.charAt(0)}</span>}
            <span><b>{slot.teacher.name}</b><small>{slot.venue.name} · {services.find(service => service.id === (slot as BookableSlot).service_id && service.teacher_id === slot.teacher.id)?.name}</small></span>
            <span aria-hidden="true">›</span>
          </button>
        ))}
      </div>
      {busyAction !== `date-${selectedDate}` && chronologicalSlots.length === 0 && <div className="pc-empty">No times remain on this date. Choose another date.</div>}
    </section>
  );

  const renderQuoteStep = () => (
    <section className="pc-wizard-card">
      <span className="pc-kicker">Step 3</span>
      <h2>Details &amp; Payment</h2>
      {!quote && <p role="status">Your balance: {overview?.private_coin_balance ?? 0} PȻ. Your PȻ cost is being calculated.</p>}
      {quote && selectedSlot && selectedService && (
        <>
          <dl className="pc-quote-details">
            <div><dt>Style</dt><dd>{selectedStyle?.name}</dd></div>
            <div><dt>Teacher</dt><dd>{selectedSlot.teacher.name}</dd></div>
            <div><dt>Date &amp; time</dt><dd>{moment.parseZone(quote.starts_at).format('D MMM YYYY, HH:mm')}</dd></div>
            <div><dt>Venue</dt><dd>{selectedSlot.venue.name}</dd></div>
            <div><dt>Private Class Option</dt><dd>{selectedService.name}</dd></div>
            <div><dt>Number of Students</dt><dd>{quote.persons_count}</dd></div>
          </dl>
          <div className="pc-quote-total">
            <span>Total</span><strong>{quote.cost} PȻ</strong>
            <small>Your balance: {quote.balance} PȻ</small>
          </div>
          {!quote.sufficient_balance && (
            <div className="pc-insufficient" role="status">
              <strong>You need {Math.max(0, quote.cost - quote.balance)} PȻ more.</strong>
              <p>Your selection will be saved. After payment, we’ll refresh the slot and get a new quote before booking.</p>
              <button className="pc-primary-button" type="button" disabled={!quote.resume_token} onClick={buyPrivateCoins}>
                Get Private Coins
              </button>
            </div>
          )}
          {quote.sufficient_balance && (
            <div className="pc-step-actions pc-payment-actions">
              <p>I agree with the <button type="button" className="pc-link-button" onClick={event => { termsTriggerRef.current = event.currentTarget; setShowBookingTerms(true); }}>booking terms</button></p>
              <button className="pc-primary-button" type="button" disabled={busyAction === 'book'} onClick={book}>
                {busyAction === 'book' ? 'Booking…' : `Confirm and pay ${quote.cost} PȻ`}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );

  const renderWizard = () => (
    <>
      {renderToolbar('Book a Private Class')}
      {renderFeedback()}
      {renderProgress()}
      {step === 'style' && <>{renderStyleStep()}{renderTeacherStep()}</>}
      {step === 'date' && <>{renderCalendar()}{selectedDate && renderTimeStep()}</>}
      {step === 'details' && renderQuoteStep()}
    </>
  );

  const historyEventTitle = (event: PrivateCoinHistoryEvent) => {
    if (event.private_class) return `${event.private_class.service_name} with ${event.private_class.teacher_name}`;
    return event.type.replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
  };

  const renderHistory = () => (
    <>
      {renderToolbar('Private Class History')}
      {renderFeedback()}
      <section className="pc-section">
        <h2>Completed &amp; cancelled classes</h2>
        {busyAction === 'history' ? <div className="pc-empty">Loading history…</div>
          : historyBookings.length ? <div className="pc-booking-list">{historyBookings.map(booking => renderBooking(booking, false))}</div>
            : <div className="pc-empty">No past private classes yet.</div>}
      </section>
      <section className="pc-section">
        <h2>Private Coin activity</h2>
        {coinHistory.length ? (
          <div className="pc-history-list">
            {coinHistory.map(event => (
              <article key={event.id}>
                <div><strong>{historyEventTitle(event)}</strong><span>{moment.parseZone(event.occurred_at).format('D MMM YYYY, HH:mm')}</span></div>
                <strong className={event.direction === 'credit' ? 'credit' : 'debit'}>
                  {event.signed_amount > 0 ? '+' : ''}{event.signed_amount} PȻ
                </strong>
              </article>
            ))}
          </div>
        ) : <div className="pc-empty">No Private Coin activity yet.</div>}
      </section>
    </>
  );

  const renderSuccess = () => (
    <>
      {renderToolbar('Booking Confirmed')}
      <section className="pc-success-card" role="status">
        <span className="pc-success-icon" aria-hidden="true">✓</span>
        <h2>Your private class is booked</h2>
        {successBooking && (
          <p>
            {successBooking.service.name} with {successBooking.teacher.name}<br />
            {moment.parseZone(successBooking.starts_at).format('dddd, D MMMM YYYY · HH:mm')}<br />
            {successBooking.venue.name}, {successBooking.room.name}
          </p>
        )}
        <button className="pc-primary-button" type="button" onClick={() => setMode('overview')}>Back to Private Classes</button>
      </section>
    </>
  );

  if (!userId || loading) {
    return <main className={`pc-page ${embedded ? 'pc-page-embedded' : ''}`}><div className="pc-empty">Loading private classes…</div></main>;
  }

  if (featureDisabled) {
    return (
      <main className={`pc-page ${embedded ? 'pc-page-embedded' : ''}`}>
        {renderToolbar('Private Classes')}
        <div className="pc-disabled"><h2>Private classes are currently unavailable</h2><p>Please check back later or contact the studio.</p></div>
      </main>
    );
  }

  if (!overview) {
    return (
      <main className={`pc-page ${embedded ? 'pc-page-embedded' : ''}`}>
        {renderToolbar('Private Classes')}
        {renderFeedback()}
        <button className="pc-primary-button pc-retry" type="button" onClick={() => window.location.reload()}>Try again</button>
      </main>
    );
  }

  return (
    <main className={`pc-page ${embedded ? 'pc-page-embedded' : ''}`}>
      {mode === 'overview' && renderOverview()}
      {mode === 'wizard' && renderWizard()}
      {mode === 'history' && renderHistory()}
      {mode === 'success' && renderSuccess()}
      {showBookingTerms && (
        <div className="info-popup-overlay" onClick={() => setShowBookingTerms(false)}>
          <section ref={termsDialogRef} className="info-popup pc-terms-popup" role="dialog" aria-modal="true" aria-labelledby="pc-terms-title" onClick={event => event.stopPropagation()}>
            <div className="info-popup-content">
              <h3 id="pc-terms-title">Private class booking terms</h3>
              {overview?.booking_terms ? <div dangerouslySetInnerHTML={{ __html: overview.booking_terms }} /> : <p>Booking terms have not been added yet.</p>}
            </div>
            <button ref={termsCloseRef} autoFocus type="button" className="info-popup-close" onClick={() => setShowBookingTerms(false)}>Close</button>
          </section>
        </div>
      )}
      {teacherInfo && (
        <div className="pc-dialog-backdrop" role="presentation" onMouseDown={() => setTeacherInfo(null)}>
          <section ref={teacherDialogRef} className="pc-dialog pc-teacher-dialog" role="dialog" aria-modal="true" aria-labelledby="pc-teacher-info-title"
            onMouseDown={event => event.stopPropagation()}>
            <h2 id="pc-teacher-info-title">{teacherInfo.name}</h2>
            <p tabIndex={0} className="pc-teacher-description">{teacherInfo.private_class_description}</p>
            <button ref={teacherCloseRef} autoFocus type="button" className="pc-secondary-button" onClick={() => setTeacherInfo(null)}>Close</button>
          </section>
        </div>
      )}
      {bookingToCancel && (
        <div className="pc-dialog-backdrop" role="presentation" onMouseDown={() => busyAction ? undefined : setBookingToCancel(null)}>
          <section
            className="pc-dialog"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="pc-cancel-title"
            aria-describedby="pc-cancel-description"
            onMouseDown={event => event.stopPropagation()}
          >
            <h2 id="pc-cancel-title">Cancel this private class?</h2>
            <p id="pc-cancel-description">
              Your eligible Private Coins will be returned minus a cancellation fee of {Math.min(bookingToCancel.cost, overview?.cancellation_fee ?? 1)} PȻ.
            </p>
            <div className="pc-dialog-actions">
              <button ref={cancelButtonRef} className="pc-secondary-button" type="button" disabled={!!busyAction} onClick={() => setBookingToCancel(null)}>
                Keep the private class
              </button>
              <button className="pc-danger-button" type="button" disabled={busyAction === `cancel-${bookingToCancel.id}`} onClick={confirmCancellation}>
                {busyAction === `cancel-${bookingToCancel.id}` ? 'Cancelling…' : 'Cancel class'}
              </button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
});

export default PrivateClasses;
